import { useOutletContext } from "react-router";
import { useCallback, useState, type ReactNode } from "react";

/** The tab of the page's Details panel. The panel opens on it by default. */
export const DETAILS_TAB_ID = "details";
/**
 * The chat tab. A page has it after Details and before its other tabs,
 * unless the page leaves it out (`setTabs(tabs, { chat: false })`).
 */
export const CHAT_TAB_ID = "chat";

export interface RightPanelTab {
  id: string;
  label: string;
  content: ReactNode;
  /** A short title line at the top of the tab, over the content. */
  title?: ReactNode;
  /** Small buttons at the right of the title line, such as Open. */
  actions?: ReactNode;
}

export interface RightPanelWorkspaceContext {
  /** The page's tabs. `chat: false` leaves chat out of the panel on the page. */
  setTabs: (tabs: RightPanelTab[], options?: { chat?: boolean }) => void;
  /** Open the right panel and switch to the named tab. */
  openTab: (tabId: string) => void;
  /** Close the right panel without changing the active tab id. */
  closePanel: () => void;
  /**
   * Toggle the named tab. When the panel is open AND that tab is active,
   * the panel closes. Otherwise the panel opens with that tab active.
   */
  toggleTab: (tabId: string) => void;
  activeTabId: string;
  /** Whether the right panel surface is currently open. */
  isOpen: boolean;
  /** True while the right panel has the wide width of `toggleWide`. */
  isWide: boolean;
  /**
   * Make the right panel about 60% of the window wide, at most
   * `ASSISTANT_MAX_WIDTH`. A second call gives back the previous width.
   */
  toggleWide: () => void;
  /** Explicit panel width, or the panel's standard width. */
  width?: number | null;
  isResizing?: boolean;
  /** Restore a saved layout without treating it as a user toggle. */
  applyLayout?: (layout: { open: boolean; tab: string; wide: boolean; width?: number | null }) => void;
}

export function useRightPanelWorkspace(): RightPanelWorkspaceContext {
  return useOutletContext<RightPanelWorkspaceContext>();
}

// A number or a UUID in a path names one record, such as an episode.
const RECORD_SEGMENT =
  /^(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * The page whose tab choice the panel remembers. The pages of one kind of
 * record share it, so the tab chosen on one episode stays for the next.
 */
export function rightPanelPageKey(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] || "/";
  const segments = path.split("/").map((segment) => (RECORD_SEGMENT.test(segment) ? ":id" : segment));
  const key = segments.join("/");
  return key.length > 1 && key.endsWith("/") ? key.slice(0, -1) : key || "/";
}

/**
 * The tab the panel shows on a page: the tab last chosen there while the
 * page still has it, then the page's Details tab, then chat. A page without
 * chat shows its first tab instead.
 */
export function resolveRightPanelTab(
  chosen: string | undefined,
  tabs: readonly Pick<RightPanelTab, "id">[],
  chat = true,
): string {
  if (chosen === CHAT_TAB_ID && chat) return chosen;
  if (chosen && tabs.some((tab) => tab.id === chosen)) return chosen;
  if (tabs.some((tab) => tab.id === DETAILS_TAB_ID)) return DETAILS_TAB_ID;
  return chat ? CHAT_TAB_ID : (tabs[0]?.id ?? CHAT_TAB_ID);
}

/**
 * The page's tabs, whether the panel has chat, and the tab the panel shows
 * on the page at `pathname`. `chooseTab` keeps a choice for the page at the
 * path it names, while the app is open.
 */
export function useRightPanelTabs(pathname: string) {
  const [page, setPage] = useState<{ tabs: RightPanelTab[]; chat: boolean }>({
    tabs: [],
    chat: true,
  });
  const { tabs, chat } = page;
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const activeTabId = resolveRightPanelTab(chosen[rightPanelPageKey(pathname)], tabs, chat);
  const setTabs = useCallback(
    (nextTabs: RightPanelTab[], options?: { chat?: boolean }) =>
      setPage({ tabs: nextTabs, chat: options?.chat ?? true }),
    [],
  );
  const chooseTab = useCallback((path: string, tabId: string) => {
    const key = rightPanelPageKey(path);
    setChosen((previous) => (previous[key] === tabId ? previous : { ...previous, [key]: tabId }));
  }, []);
  return { tabs, chat, setTabs, activeTabId, chooseTab };
}
